import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

import { ME_QUERY_KEY } from "../auth/session";
import { ApiError } from "./client";

/**
 * 401 or 403 on any request means who you are, or what you may do, changed on the server
 * (signed out elsewhere, session expired, role removed). Re-check "me": RequireAuth then sends
 * a signed-out person to sign in, and menus drop anything the new roles don't allow.
 */
function recheckSession(client: QueryClient, error: unknown): void {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    void client.invalidateQueries({ queryKey: ME_QUERY_KEY });
  }
}

function isMeQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === ME_QUERY_KEY[0];
}

export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // The "me" query failing is handled by RequireAuth; re-checking it here would loop.
        if (!isMeQuery(query.queryKey)) recheckSession(client, error);
      },
    }),
    mutationCache: new MutationCache({ onError: (error) => recheckSession(client, error) }),
    defaultOptions: {
      queries: {
        // Only retry what retrying can fix (network blips, 5xx); never 4xx.
        retry: (count, error) => !(error instanceof ApiError && !error.isRetryable) && count < 2,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  });
  return client;
}
